// The status flag uses ActionPillMenu for the shared pill cascade and app-wide menu coordination.

import React, { useState } from 'react';
import {
    describeStatusCircle,
    FLAG_MENU_OPTIONS,
    isTerminalFlagStatus,
    normalizeFlagStatus,
    shouldPulseStatusIcon,
} from '../../lib/communicationsFlags';
import ActionPillMenu from '../ui/ActionPillMenu';

export function CommunicationsStatusIcon({ session, className = '', bare = false }) {
    // Gate 17c: a closed conversation's circle shows only the Closed state, in grey, no pulse.
    const flag = describeStatusCircle(session);
    const pulsing = shouldPulseStatusIcon(session);
    const sizeCls = bare ? 'h-5 w-5 text-sm' : (flag.set ? 'h-5 w-5 text-sm' : 'h-[29px] w-[29px] text-base');
    return (
        <span className={`relative inline-flex items-center justify-center rounded-full leading-none ${sizeCls} ${flag.className} ${className}`}>
            {pulsing && <span aria-hidden="true" className="absolute inset-0 rounded-full border border-current opacity-60 animate-ping" />}
            <span className="relative z-10">{flag.glyph}</span>
        </span>
    );
}

function CommunicationsThreadFlagControl({ session, onSetFlag, disabled = false, theme = 'light' }) {
    const dark = theme === 'dark';
    const [saving, setSaving] = useState(false);
    const [dialogOption, setDialogOption] = useState(null);
    const [dialogStatus, setDialogStatus] = useState('none');
    const [dialogActive, setDialogActive] = useState(false);
    const [dialogNote, setDialogNote] = useState('');

    const flag = describeStatusCircle(session);
    const buttonLabel = flag.set ? `Status: ${flag.label}. Change status` : 'Set status';
    const emptyFlagCls = dark
        ? 'text-gray-400 hover:bg-gray-700/60 hover:text-gray-100'
        : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700';

    const choose = (option) => {
        setDialogOption(option);
        setDialogStatus(option.status);
        setDialogActive(isTerminalFlagStatus(option.status) ? false : Boolean(session?.flag_active));
        setDialogNote('');
    };

    const saveDialog = async () => {
        if (!onSetFlag || !dialogOption) return;
        const nextStatus = normalizeFlagStatus(dialogStatus);
        const nextActive = isTerminalFlagStatus(nextStatus) ? false : dialogActive;
        setSaving(true);
        try {
            await onSetFlag({ status: nextStatus, urgency: null, active: nextActive, note: dialogNote });
            setDialogOption(null);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
        <ActionPillMenu
            placement="left"
            mobilePlacement="left"
            menuLabel="Set thread status"
            menuClassName="communications-status-menu"
            triggerLabel={buttonLabel}
            triggerTitle={flag.set ? flag.label : 'Set status'}
            disabled={disabled || saving}
            triggerClassName={`${flag.set ? 'h-5 w-5 text-sm' : 'h-[29px] w-[29px] text-base'} rounded-full leading-none ${flag.set ? `ring-1 ${flag.className} ring-current` : emptyFlagCls} ${disabled || saving ? 'cursor-not-allowed' : ''}`}
            wrapperProps={{
                'data-thread-flag-control': 'true',
                style: { position: 'absolute', right: '0.5rem', top: '0.5rem' },
            }}
            options={FLAG_MENU_OPTIONS.map((option) => ({
                key: option.key,
                label: option.label,
                icon: option.glyph,
                iconClassName: option.className,
                active: flag.status === option.status,
                onSelect: () => choose(option),
            }))}
        >
            <CommunicationsStatusIcon session={session} bare />
        </ActionPillMenu>
            {dialogOption && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Set thread status"
                    className={`absolute top-0 right-[35px] z-50 w-64 rounded-lg border p-3 text-left shadow-xl ${dark ? 'bg-gray-800 border-gray-600 text-gray-100' : 'bg-white border-gray-200 text-gray-800'}`}
                    onClick={(event) => event.stopPropagation()}
                >
                    <label className="block text-xs font-medium">
                        Status
                        <select
                            value={dialogStatus}
                            disabled={saving}
                            onChange={(event) => {
                                const next = event.target.value;
                                setDialogStatus(next);
                                if (isTerminalFlagStatus(next)) setDialogActive(false);
                            }}
                            className={`mt-1 w-full rounded border px-2 py-1 text-sm ${dark ? 'bg-gray-900 border-gray-600' : 'bg-white border-gray-300'}`}
                        >
                            {FLAG_MENU_OPTIONS.map((option) => (
                                <option key={option.key} value={option.status}>{option.label}</option>
                            ))}
                        </select>
                    </label>
                    <label className="mt-3 flex items-center gap-2 text-xs">
                        <input
                            type="checkbox"
                            checked={!isTerminalFlagStatus(dialogStatus) && dialogActive}
                            disabled={saving || isTerminalFlagStatus(dialogStatus)}
                            onChange={(event) => setDialogActive(event.target.checked)}
                            aria-label="Currently active"
                        />
                        <span>Currently active</span>
                    </label>
                    <label className="mt-3 block text-xs font-medium">
                        Note
                        <textarea
                            value={dialogNote}
                            disabled={saving}
                            onChange={(event) => setDialogNote(event.target.value)}
                            className={`mt-1 min-h-20 w-full rounded border px-2 py-1 text-sm ${dark ? 'bg-gray-900 border-gray-600' : 'bg-white border-gray-300'}`}
                            aria-label="Status note"
                        />
                    </label>
                    <div className="mt-3 flex justify-end gap-2">
                        <button type="button" disabled={saving} onClick={() => setDialogOption(null)} className="rounded border px-2 py-1 text-xs">
                            Cancel
                        </button>
                        <button type="button" disabled={saving} onClick={saveDialog} className="rounded bg-[var(--btn-fill)] px-2 py-1 text-xs font-medium text-[var(--btn-ink)]">
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}

export default CommunicationsThreadFlagControl;
